import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle, WarningCircle, ArrowsClockwise } from "@phosphor-icons/react";

const Row = ({ ok, label, detail, testId }) => (
  <div className="flex items-start gap-2 text-sm" data-testid={testId}>
    {ok ? <CheckCircle size={18} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" /> : <WarningCircle size={18} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />}
    <div><span className="font-medium">{label}</span>{detail && <span className="text-muted-foreground"> — {detail}</span>}</div>
  </div>
);

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "never");

export const FedexHealthCard = () => {
  const [tracking, setTracking] = useState("");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/fedex/diagnostics", { params: tracking.trim() ? { tracking: tracking.trim() } : {} });
      setD(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Diagnostics failed"); } finally { setBusy(false); }
  };

  const markFedex = async (id) => {
    try { await api.post(`/fedex/shipments/${id}/mark-fedex`); toast.success("Carrier set to FedEx — will sync every 2 hours"); run(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const lastRun = d?.last_cron_runs?.[0];
  return (
    <div className="hp-card p-6 space-y-4" data-testid="fedex-health-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-semibold">FedEx health check</h3>
        <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={run} disabled={busy} data-testid="fedex-diag-run">
          <ArrowsClockwise size={14} className={`mr-1.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "Checking…" : "Run check"}
        </Button>
      </div>
      <div className="flex gap-2">
        <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Optional: FedEx tracking number to probe (e.g. 877500391190)" className="h-9" data-testid="fedex-diag-tracking" />
      </div>
      {d && (
        <div className="space-y-2">
          <Row ok={d.configured} label={`Environment: ${d.env}`} detail={d.configured ? `account …${d.account_tail}` : "API key / secret / account missing"} testId="fedex-diag-env" />
          <Row ok={d.auth_ok} label="FedEx login (OAuth)" detail={d.auth_ok ? "token issued" : d.auth_error} testId="fedex-diag-auth" />
          <Row ok={d.cron_secret_set} label="Cron secret" detail={d.cron_secret_set ? "set" : "WEBHOOK_CRON_SECRET missing — the 2-hourly sync cannot run"} testId="fedex-diag-cron-secret" />
          {d.ship_api && <Row ok={d.ship_api.ok} label="Ship API (create / cancel AWB)" detail={d.ship_api.ok ? `authorised${d.ship_api.alerts?.length ? ` · ${d.ship_api.alerts.join("; ")}` : ""}` : d.ship_api.error} testId="fedex-diag-ship" />}
          {d.ship_api?.hint && <p className="text-xs rounded-sm border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 ml-6" data-testid="fedex-diag-ship-hint">{d.ship_api.hint}</p>}
          {d.pickup_api && <Row ok={d.pickup_api.ok} label="Pickup API (book / cancel pickups)" detail={d.pickup_api.ok ? `authorised · ${d.pickup_api.options} pickup option(s) available${d.pickup_api.detail ? ` · ${d.pickup_api.detail}` : ""}` : d.pickup_api.error} testId="fedex-diag-pickup" />}
          {d.pickup_api?.hint && <p className="text-xs rounded-sm border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 ml-6" data-testid="fedex-diag-pickup-hint">{d.pickup_api.hint}</p>}
          {d.recent_pickups?.length > 0 && (
            <div className="text-xs text-muted-foreground ml-6" data-testid="fedex-diag-pickups">
              Recent pickups: {d.recent_pickups.map((p) => `${p.confirmation_code} (${p.scheduled_date}, ${p.status}${p.env !== d.env ? `, booked in ${p.env}` : ""})`).join(" · ")}
            </div>
          )}
          <Row ok={!!lastRun && lastRun.status === "done" && !lastRun.errors} label="Last auto-sync" detail={lastRun ? `${fmt(lastRun.at)} · checked ${lastRun.checked ?? "?"} · updated ${lastRun.changed ?? 0} · errors ${lastRun.errors ?? 0}${lastRun.last_error ? ` · ${lastRun.last_error}` : ""}` : "no run recorded yet"} testId="fedex-diag-cron" />
          {d.probe && <Row ok={d.probe.found && !d.probe.error} label={`Tracking probe ${d.probe.tracking}`} detail={d.probe.error || `${d.probe.latest_code || "—"} ${d.probe.latest_status || ""} · ${d.probe.scans} scans`} testId="fedex-diag-probe" />}
          <Row ok label={`${d.auto_synced.length} FedEx shipment(s) in auto-sync`} detail={d.stale_over_60_days ? `${d.stale_over_60_days} older than 60 days are skipped` : ""} testId="fedex-diag-count" />
          {d.auto_synced.filter((s) => s.fedex?.last_error).map((s) => (
            <Row key={s.id} ok={false} label={<Link to={`/shipments/${s.id}`} className="underline">{s.shipment_number}</Link>} detail={`${s.tracking_number}: ${s.fedex.last_error}`} testId={`fedex-diag-err-${s.shipment_number}`} />
          ))}
          {d.looks_fedex_but_not_synced.length > 0 && (
            <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="fedex-diag-unsynced">
              <p className="text-sm text-amber-900 font-medium">{d.looks_fedex_but_not_synced.length} shipment(s) have a FedEx-looking number but carrier is not FedEx — they are not being synced:</p>
              {d.looks_fedex_but_not_synced.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-2 text-sm">
                  <span><Link to={`/shipments/${s.id}`} className="underline">{s.shipment_number}</Link> · {s.tracking_number} · carrier: {s.carrier || "none"}</span>
                  <Button size="sm" variant="outline" className="h-7 rounded-sm" onClick={() => markFedex(s.id)} data-testid={`fedex-mark-${s.shipment_number}`}>Set FedEx</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
